import { useMemo } from "react";
import { Link } from "react-router-dom";
import { getDaysInMonth } from "date-fns";
import { Wrench, CalendarClock, CalendarCheck2, Rows3 } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { EmptyState } from "@/components/EmptyState";
import { StatusDot } from "@/components/StatusPill";
import { MONTHS_SHORT_NN, KIND_LABEL, fromISO } from "@/lib/calendar";
import { formatDate, daysText } from "@/lib/format";
import { cn } from "@/lib/utils";
import { EventActionsItems } from "./EventActions";

const pct = (iso) => {
  const d = fromISO(iso);
  return ((d.getDate() - 0.5) / getDaysInMonth(d)) * 100;
};

const Marker = ({ e, planned = false, onPlan, onMove, onUnplan }) => {
  const iso = planned ? e.planned_date : e.date;
  const Icon = planned ? CalendarCheck2 : e.kind === "annual" ? CalendarClock : Wrench;
  return (
    <DropdownMenu modal={false}>
      <Tooltip delayDuration={100}>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              style={{ left: `${pct(iso)}%` }}
              data-testid={`timeline-marker-${e.machine_id}-${e.kind}${planned ? "-planned" : ""}`}
              aria-label={`${e.label} · ${planned ? "Planlagt" : KIND_LABEL[e.kind]} ${formatDate(iso)}`}
              className={cn(
                "absolute top-1/2 z-10 flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-card shadow-sm",
                "transition-transform duration-150 hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60",
                planned ? "cal-fill-planned border-dashed" : `cal-fill-${e.status}`
              )}
            >
              <Icon className="h-3.5 w-3.5" strokeWidth={2.5} />
            </button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          <div className="font-semibold">{e.label}</div>
          <div>{planned ? `Planlagt ${formatDate(iso)} (frist ${formatDate(e.date)})` : `${KIND_LABEL[e.kind]} · ${formatDate(iso)} · ${daysText(e.days_remaining)}`}</div>
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="start" className="w-64 p-1.5" data-testid={`timeline-actions-${e.machine_id}-${e.kind}`}>
        <EventActionsItems e={e} onPlan={onPlan} onMove={onMove} onUnplan={onUnplan} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const MachineTimelineView = ({ year, machines, events, today, onPlan, onMove, onUnplan }) => {
  const byMachine = useMemo(() => {
    const m = new Map();
    for (const e of events) {
      if (!m.has(e.machine_id)) m.set(e.machine_id, []);
      m.get(e.machine_id).push(e);
    }
    return m;
  }, [events]);

  const groups = useMemo(() => {
    const map = new Map();
    for (const mc of machines) {
      const key = mc.customer_id || "ukjend";
      if (!map.has(key)) map.set(key, { customer_id: mc.customer_id, customer_name: mc.customer_name || "Ukjend kunde", rows: [] });
      map.get(key).rows.push(mc);
    }
    return [...map.values()];
  }, [machines]);

  if (machines.length === 0) {
    return <EmptyState icon={Rows3} title="Ingen maskiner" description="Ingen maskiner passar filteret." />;
  }

  const yearStr = String(year);
  const todayDate = fromISO(today);
  const todayInYear = todayDate.getFullYear() === year;

  return (
    <TooltipProvider>
      <div className="overflow-hidden rounded-2xl border bg-card shadow-sm" data-testid="calendar-machines-view">
        <div className="app-scroll overflow-x-auto">
          <div className="min-w-[880px]">
            {/* Header */}
            <div className="grid grid-cols-[220px_repeat(12,minmax(0,1fr))] border-b bg-muted/50 sm:grid-cols-[260px_repeat(12,minmax(0,1fr))]">
              <div className="sticky left-0 z-20 border-r bg-muted/50 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground backdrop-blur">
                Maskin · {year}
              </div>
              {MONTHS_SHORT_NN.map((m, i) => (
                <div
                  key={m}
                  className={cn(
                    "border-r py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-muted-foreground last:border-r-0",
                    todayInYear && todayDate.getMonth() === i && "text-primary"
                  )}
                >
                  {m}
                </div>
              ))}
            </div>

            {groups.map((g) => (
              <div key={g.customer_id || "ukjend"} data-testid={`timeline-customer-${g.customer_id || "ukjend"}`}>
                <div className="grid grid-cols-[220px_repeat(12,minmax(0,1fr))] border-b bg-muted/25 sm:grid-cols-[260px_repeat(12,minmax(0,1fr))]">
                  <div className="sticky left-0 z-20 col-span-1 border-r bg-muted/25 px-3 py-1.5 font-heading text-xs font-bold backdrop-blur">
                    {g.customer_name}
                    <span className="ml-1.5 font-body text-[11px] font-medium text-muted-foreground">{g.rows.length} {g.rows.length === 1 ? "maskin" : "maskiner"}</span>
                  </div>
                  <div className="col-span-12" />
                </div>

                {g.rows.map((mc) => {
                  const evs = byMachine.get(mc.id) || [];
                  const inYear = evs.filter((e) => (e.date || "").startsWith(yearStr));
                  const plannedInYear = evs.filter((e) => (e.planned_date || "").startsWith(yearStr));
                  const next = evs.map((e) => e.date).filter(Boolean).sort()[0];
                  return (
                    <div key={mc.id} className="group grid grid-cols-[220px_repeat(12,minmax(0,1fr))] border-b last:border-b-0 hover:bg-accent/40 sm:grid-cols-[260px_repeat(12,minmax(0,1fr))]" data-testid={`timeline-row-${mc.id}`}>
                      <div className="sticky left-0 z-20 flex min-h-[56px] items-center gap-2.5 border-r bg-card px-3 py-2 backdrop-blur group-hover:bg-accent/40">
                        <StatusDot status={mc.overall_status} className="shrink-0" />
                        <div className="min-w-0">
                          <Link to={`/maskiner/${mc.id}`} className="block truncate text-sm font-semibold hover:underline" data-testid={`timeline-machine-link-${mc.id}`}>{mc.label}</Link>
                          <div className="truncate text-[11px] text-muted-foreground">
                            {next ? `Neste frist ${formatDate(next)}` : "Ingen fristar registrert"}
                            {inYear.length === 0 && next && !next.startsWith(yearStr) ? ` (${next.slice(0, 4)})` : ""}
                          </div>
                        </div>
                      </div>
                      {MONTHS_SHORT_NN.map((_, mi) => {
                        const key = `${yearStr}-${String(mi + 1).padStart(2, "0")}`;
                        const dues = inYear.filter((e) => e.date.startsWith(key));
                        const plans = plannedInYear.filter((e) => e.planned_date.startsWith(key));
                        const isTodayMonth = todayInYear && todayDate.getMonth() === mi;
                        return (
                          <div key={mi} className={cn("relative border-r last:border-r-0", isTodayMonth && "bg-primary/[0.04]")}>
                            <div className="absolute inset-x-0 top-1/2 h-px bg-border" aria-hidden="true" />
                            {isTodayMonth && (
                              <span className="absolute inset-y-0 w-px bg-primary/70" style={{ left: `${pct(today)}%` }} aria-hidden="true" />
                            )}
                            {plans.map((e) => (
                              <Marker key={`${e.id}-p`} e={e} planned onPlan={onPlan} onMove={onMove} onUnplan={onUnplan} />
                            ))}
                            {dues.map((e) => (
                              <Marker key={e.id} e={e} onPlan={onPlan} onMove={onMove} onUnplan={onUnplan} />
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t bg-muted/30 px-3 py-2 text-[11px] text-muted-foreground">
          <span className="flex items-center gap-1.5"><span className="cal-fill-ok inline-flex h-4 w-4 items-center justify-center rounded-full"><Wrench className="h-2.5 w-2.5" /></span> Service</span>
          <span className="flex items-center gap-1.5"><span className="cal-fill-ok inline-flex h-4 w-4 items-center justify-center rounded-full"><CalendarClock className="h-2.5 w-2.5" /></span> Årskontroll</span>
          <span className="flex items-center gap-1.5"><span className="cal-fill-planned inline-flex h-4 w-4 items-center justify-center rounded-full border border-dashed border-card"><CalendarCheck2 className="h-2.5 w-2.5" /></span> Planlagt</span>
          <span className="ml-auto hidden sm:inline">Klikk på ein markør for handlingar. Fargen viser status.</span>
        </div>
      </div>
    </TooltipProvider>
  );
};
